import React from 'react';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getSelectedItemIdentifiers } from '../../../library/selection/selectors';
import { SelectedItemIdentifier } from '../../../library/selection/types';
import { RightClickedItem } from '../../../store/slices/viewportSlice';
import { useAppSelector } from '../../hooks';
import { SceneMenuWrapper } from './common/SceneMenuWrapper';
import { BearingEnvelopesContainerMenu } from './containers/BearingEnvelopesContainerMenu';
import { BearingsContainerMenu } from './containers/BearingsContainerMenu';
import { BracesContainerMenu } from './containers/BracesContainerMenu';
import { CeilingPlanesContainerMenu } from './containers/CeilingPlanesContainerMenu';
import { CeilingTraysContainerMenu } from './containers/CeilingTraysContainerMenu';
import { ComponentDesignsContainerMenu } from './containers/ComponentDesignsContainerMenu';
import { FeaturePatternContainersContainerMenu } from './containers/FeaturePatternContainersContainerMenu';
import { GussetsContainerMenu } from './containers/GussetsContainerMenu';
import { HangerContainerMenu } from './containers/HangerContainerMenu';
import { JackGirderConnectionsContainerMenu } from './containers/JackGirderConnectionsContainerMenu';
import { LoadCasesContainerMenu } from './containers/LoadCasesContainerMenu';
import { ManualLoadsContainerMenu } from './containers/ManualLoadsContainerMenu';
import { MembersContainerMenu } from './containers/MembersContainerMenu';
import { MovingLoadsContainerMenu } from './containers/MovingLoadsContainerMenu';
import { ParapetLoadsContainerMenu } from './containers/ParapetLoadsContainerMenu';
import { PlatePairsContainerMenu } from './containers/PlatePairsContainerMenu';
import { ReferenceLinesContainerMenu } from './containers/ReferenceLinesContainerMenu';
import { RoofPlanesContainerMenu } from './containers/RoofPlanesContainerMenu';
import { SplicesContainerMenu } from './containers/SplicesContainerMenu';
import { TrussEnvelopesContainerMenu } from './containers/TrussEnvelopesContainerMenu';
import { TrussGroupsContainerMenu } from './containers/TrussGroupsContainerMenu';
import { WorkLinesContainerMenu } from './containers/WorkLinesContainerMenu';
import { AdditionalLoadsMenu } from './other-items/AdditionalLoadsMenu';
import { BuildingMenu } from './other-items/BuildingMenu';
import { LevelMenu } from './other-items/LevelMenu';
import { ProjectMenu } from './other-items/ProjectMenu';
import { TrussGroupMenu } from './other-items/TrussGroupMenu';
import { AnalogModelMenu } from './scene-items/AnalogModelMenu';
import { BearingEnvelopeMenu, BulkBearingEnvelopeMenu } from './scene-items/BearingEnvelopeMenu';
import { BearingMenu } from './scene-items/BearingMenu';
import { CeilingPlaneMenu } from './scene-items/CeilingPlaneMenu';
import { CeilingTrayMenu } from './scene-items/CeilingTrayMenu';
import { ComponentDesignMenu } from './scene-items/ComponentDesignMenu';
import { DefaultMenu } from './scene-items/DefaultMenu';
import { FeatureAreaMenu } from './scene-items/FeatureAreaMenu';
import {
  BulkFeaturePatternContainerMenu,
  FeaturePatternContainerMenu,
} from './scene-items/FeaturePatternContainerMenu';
import { HangerMenu } from './scene-items/HangerMenu';
import { MemberMenu } from './scene-items/MemberMenu';
import { PlatePairMenu } from './scene-items/PlatePairMenu';
import { ReferenceLineMenu } from './scene-items/ReferenceLineMenu';
import { RoofContainerMenu } from './scene-items/RoofContainerMenu';
import { RoofPlaneMenu } from './scene-items/RoofPlaneMenu';
import { SnappingUnderlayMenu } from './scene-items/SnappingUnderlayMenu';
import { BulkTrussEnvelopeMenu, TrussEnvelopeMenu } from './scene-items/TrussEnvelopeMenu';
import { WebJointSegmentMenu } from './scene-items/WebJointSegmentMenu';
import { WebSegmentMenu } from './scene-items/WebSegmentMenu';
import { WorkLineMenu } from './scene-items/WorkLineMenu';

export default function SceneMenu() {
  const item = useAppSelector((state) => state.viewport.rightClickedItem);
  const selectedItems = useAppSelector(getSelectedItemIdentifiers);

  if (item == null) return null;

  let matchingSelectedItems: SelectedItemIdentifier[] = [];

  if (item.type && ('guid' in item || 'guids' in item)) {
    const allRightClickedItemsAreSelected =
      'guids' in item
        ? selectedItems.every((selectedItem) => item.guids.some((guid) => selectedItem.guid === guid))
        : selectedItems.some((selectedItem) => selectedItem.guid === item.guid);

    const allSelectedItemsAreSameTypeAsRightClickedItem = selectedItems.every(
      (selectedItem) => selectedItem.type === item.type,
    );

    if (allRightClickedItemsAreSelected && allSelectedItemsAreSameTypeAsRightClickedItem) {
      matchingSelectedItems = selectedItems;
    }
  }

  return (
    <SceneMenuWrapper requestedStartingReplacement={item.requestedStartingReplacement}>
      <SceneMenuContentRenderer item={item} matchingSelectedItems={matchingSelectedItems} />
    </SceneMenuWrapper>
  );
}

interface RendererProps {
  item: RightClickedItem;
  matchingSelectedItems: SelectedItemIdentifier[];
}

function SceneMenuContentRenderer({ item, matchingSelectedItems }: RendererProps) {
  switch (item.type) {
    // special cases:

    case 'referenceLine':
      return <ReferenceLineMenu guids={item.guids} position={item.position} />;

    case null:
      return <DefaultMenu />;

    // regular items:

    case 'additionalLoads':
      return <AdditionalLoadsMenu />;

    case 'analogModel':
      return <AnalogModelMenu /* always targets analog model of active component design */ />;

    case 'bearing':
      return <BearingMenu guid={item.guid} />;

    case 'beam':
    case 'wall':
      return matchingSelectedItems.length > 1 ? (
        <BulkBearingEnvelopeMenu type={item.type} items={matchingSelectedItems} />
      ) : (
        <BearingEnvelopeMenu guid={item.guid} />
      );

    case 'building':
      return <BuildingMenu guid={item.guid} />;

    case 'ceilingPlane':
      return <CeilingPlaneMenu items={matchingSelectedItems} />;

    case 'ceilingTray':
      return <CeilingTrayMenu items={matchingSelectedItems} />;

    case 'componentDesign':
      return <ComponentDesignMenu guid={item.guid} matchingSelectedItems={matchingSelectedItems} />;

    case 'featureArea':
      return <FeatureAreaMenu items={matchingSelectedItems} />;

    case 'featurePatternContainer':
      return matchingSelectedItems.length > 1 ? (
        <BulkFeaturePatternContainerMenu items={matchingSelectedItems} />
      ) : (
        <FeaturePatternContainerMenu guid={item.guid} />
      );

    case 'hanger':
      return <HangerMenu guid={item.guid} />;

    case 'level':
      return <LevelMenu guid={item.guid} />;

    case 'member':
      return <MemberMenu guid={item.guid} position={item.position} matchingSelectedItems={matchingSelectedItems} />;

    case 'platePair':
      return <PlatePairMenu guid={item.guid} matchingSelectedItems={matchingSelectedItems} />;

    case 'project':
      return <ProjectMenu guid={item.guid} />;

    case 'roofContainer':
      return <RoofContainerMenu guid={item.guid} />;

    case 'roofPlane':
      return <RoofPlaneMenu items={matchingSelectedItems} />;

    case 'snappingUnderlay':
      return <SnappingUnderlayMenu guid={item.guid} />;

    case 'trussEnvelope':
      return matchingSelectedItems.length > 1 ? (
        <BulkTrussEnvelopeMenu items={matchingSelectedItems} />
      ) : (
        <TrussEnvelopeMenu guid={item.guid} />
      );

    case 'trussGroup':
      return <TrussGroupMenu guid={item.guid} />;

    case 'webSegment':
      return <WebSegmentMenu guid={item.guid} />;

    case 'webJointSegment':
      return <WebJointSegmentMenu guid={item.guid} />;

    case 'workLine':
      return <WorkLineMenu guid={item.guid} matchingSelectedItems={matchingSelectedItems} />;

    // containers:

    case 'bearingContainer':
      return <BearingsContainerMenu />;

    case 'beamContainer':
      return <BearingEnvelopesContainerMenu type="beam" />;

    case 'wallContainer':
      return <BearingEnvelopesContainerMenu type="wall" />;

    case 'braceContainer':
      return <BracesContainerMenu />;

    case 'ceilingPlaneContainer':
      return <CeilingPlanesContainerMenu />;

    case 'ceilingTrayContainer':
      return <CeilingTraysContainerMenu />;

    case 'componentDesignContainer':
      return <ComponentDesignsContainerMenu />;

    case 'featurePatternContainerContainer':
      return <FeaturePatternContainersContainerMenu />;

    case 'gussetContainer':
      return <GussetsContainerMenu />;

    case 'jackGirderConnectionContainer':
      return <JackGirderConnectionsContainerMenu />;

    case 'hangerContainer':
      return <HangerContainerMenu />;

    case 'loadCaseContainer':
      return <LoadCasesContainerMenu />;

    case 'manualLoadContainer':
      return <ManualLoadsContainerMenu />;

    case 'memberContainer':
      return <MembersContainerMenu />;

    case 'movingLoadContainer':
      return <MovingLoadsContainerMenu />;

    case 'parapetLoadContainer':
      return <ParapetLoadsContainerMenu />;

    case 'platePairContainer':
      return <PlatePairsContainerMenu />;

    case 'referenceLineContainer':
      return <ReferenceLinesContainerMenu />;

    case 'roofPlaneContainer':
      return <RoofPlanesContainerMenu />;

    case 'spliceContainer':
      return <SplicesContainerMenu />;

    case 'trussEnvelopeContainer':
      return <TrussEnvelopesContainerMenu />;

    case 'trussGroupContainer':
      return <TrussGroupsContainerMenu />;

    case 'workLineContainer':
      return <WorkLinesContainerMenu />;

    default:
      assertExhaustiveSwitch(item);
  }
}
